import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { Button, Glyphicon, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { DataTable, Status, PageHeader, SearchBar, PopOver } from 'axway-react-ui';
import IntlComponent from 'axway-react-intl';
import Fuse from 'fuse.js';
import browserHistory from 'components/utility/history';

export class APIsView extends React.Component {
	constructor(props) {
		super(props);
		IntlComponent.init(this);
		this.setSearchFilter = this.setSearchFilter.bind(this);
		this.applySearchFilter = this.applySearchFilter.bind(this);
		this.renderImportButtons = this.renderImportButtons.bind(this);
		this.state = {};

		// distance x threshold = no. of chars it will search within (starting from the left)
		this.fuseOpts = {
			threshold: 0.1,
			distance: 1000,
			keys: [ 'name', 'description' ]
		};
		this.fuse = new Fuse(props.items, this.fuseOpts);
	}

	setSearchFilter(filter) {
		let searchFilter;
		if (filter) {
			searchFilter = filter;
		}
		this.setState({
			searchFilter
		});
	}

	applySearchFilter(items) {
		if (this.state.searchFilter) {
			return this.fuse.search(this.state.searchFilter);
		}
		return items;
	}

	renderImportButtons() {
		const { apiSpecTypes, onImportApi } = this.props;
		const buttons = [];
		for (const type in apiSpecTypes) {
			const name = type === 'endpoint' ? 'API' : apiSpecTypes[type].name;
			buttons.push(
				<Button
					key={type}
					bsSize="large"
					bsStyle="primary"
					className="compact"
					onClick={() => onImportApi(type)}
				>
					<Glyphicon glyph=" gg icon-GG-plus" />
					{name}
				</Button>
			);
		}
		return buttons;
	}

	componentWillReceiveProps(props) {
		this.fuse = new Fuse(props.items, this.fuseOpts);
	}

	stopPropagation(event) {
		event.stopPropagation();
	}

	render() {
		const { items, apiSpecTypes } = this.props;
		const apis = this.applySearchFilter(items);
		// format and sort the apis

		// REFACTOR (AH): pull out link formatting and remove this map
		const rows = apis.map(item => {
			const id = encodeURIComponent(item.id);
			return {
				name: item.name,
				description: item.description,
				type: item.type,
				link: `${window.$conf.prefix}/project/apidocs/${id}`,
				url: item.url,
				status: item.status
			};
		});

		rows.sort((a, b) => a.name.localeCompare(b.name));

		const columns = [
			{
				key: 'name',
				name: this.intl.formatMsg('Name'),
				className: 'name nowrap',
				format: (item) => {
					return (
						<a
							href={item.link}
							onClick={(e) => e.preventDefault()}
						>
							{item.name}
						</a>
					);
				}
			},
			{
				key: 'description',
				name: this.intl.formatMsg('Description'),
				className: 'description nowrap ellipsis'
			},
			{
				key: 'type',
				name: this.intl.formatMsg('Type'),
				className: 'type nowrap',
				format: item => {
					if (item.type === 'endpoint') {
						const title = this.intl.formatMsg('Endpoint');
						const overlay = (
							<Tooltip id="tooltip">
								{this.intl.formatMsg('Endpoints are deprecated and have been replaced with the OpenAPI flow-trigger plugin.')}
							</Tooltip>
						);

						return (
							<div className="endpoint">
								<OverlayTrigger
									placement="left"
									rootClose
									overlay={overlay}
								>
									<div tabIndex={0}>
										{title}
										<span className="gg icon-GG-warning" />
									</div>
								</OverlayTrigger>
							</div>
						);
					} else if (item.type === 'dynamic') {
						const infoOverlay = (
							<PopOver title={this.intl.formatMsg('Dynamic OpenAPI')}>
								{this.intl.formatMsg('This OpenAPI specification is dynamically generated for your service. It contains documentation for programmatically defined APIs, routes, and dynamic APIs, such as those automatically generated from models.')}
							</PopOver>
						);
						return (
							<div>
								{this.intl.formatMsg('Dynamic')}
								<OverlayTrigger
									placement="left"
									trigger="click"
									rootClose
									overlay={infoOverlay}
								>
									<Button
										className="gg icon-GG-info text"
										onClick={this.stopPropagation}
										onKeyPress={this.stopPropagation}
									/>
								</OverlayTrigger>
							</div>
						);
					} else {
						return apiSpecTypes[item.type].name;
					}
				}
			},
			{
				key: 'url',
				name: this.intl.formatMsg('Download URL'),
				className: 'url nowrap ellipsis',
				format: (item) => {
					if (!item.url) {
						return;
					}
					const url = new URL(item.url);
					let linkText = url.href;

					// If the url seems like it's hosted by API Builder, strip the host to
					// save space. This check is naive and could be improved by checking the port
					// to see if the host is actually the current API Builder service, or if it's
					// a different service.
					if (url.hostname === 'localhost') {
						linkText = url.pathname + url.search;
					}

					return (
						<a href={url.href} download onClick={this.stopPropagation}>
							{linkText}
						</a>
					);
				}
			},
			{
				key: 'status',
				name: this.intl.formatMsg('Status'),
				className: 'status',
				format: item => {
					const enabled = item.status ? !!item.status.enabled : true;
					const errors = item.status && item.status.errors ? item.status.errors : [];
					const warnings = item.status && item.status.warnings
						? item.status.warnings : [];
					if (!errors.length && !warnings.length && enabled) {
						return (
							<Status type="enabled" />
						);
					}
					if (!enabled && !errors.length) {
						return (
							<Status
								type="disabled"
								tooltipPlacement="bottom"
								tooltip={this.intl.formatMsg('Disabled at source')}
							/>
						);
					}
					if (errors.length) {
						return (
							<Status
								type="error"
								tooltipPlacement="left"
								tooltip={this.intl.formatMsg('There was an error loading this API. Please check the logs.')}
							/>
						);
					} else if (warnings.length) {
						let tooltip;
						if (item.type === 'endpoint') {
							tooltip = this.intl.formatMsg('There was an error loading one or more flows from this endpoint. Please check the logs.');
						} else {
							tooltip = this.intl.formatMsg('There is something wrong with this API. Please check the logs.');
						}
						return (
							<Status
								type="warning"
								tooltipPlacement="left"
								tooltip={tooltip}
							/>
						);
					}
				}
			}
		];

		const clickRow = (e, item) => {
			if (item.link) {
				browserHistory.push(item.link);
			}
		};

		let info;
		let link;
		if (window.$conf.endpointsEnabled) {
			info = this.intl.formatMsg('Below are the API specifications that your service hosts. These specifications can be used by clients to access your service. You can view the specifications and try them from this UI. To get started with API development, click the +API button. Note that an upgrade is available. Please follow the link below to learn how to upgrade.', 'endpoints_info');
			link = window.$conf.docUpgradeEndpoints;
		} else {
			info = this.intl.formatMsg('Below are the API specifications that your service hosts. These specifications can be used by clients to access your service. You can view the specifications and try them from this UI. To get started with API development, follow the guide in the link below to import an OpenAPI specification.', 'specs_info');
			link = window.$conf.docManageSpecs;
		}

		return (
			<div className="apis-view">
				<PageHeader>
					<PageHeader.Section align="left">
						<PageHeader.Title
							infoPopover={{
								id: 'apis-info',
								description: info,
								docLink: link
							}}
						>
							{this.intl.formatMsg('API Doc & Test')}
						</PageHeader.Title>
					</PageHeader.Section>
					<PageHeader.Section>
						<SearchBar
							onChange={this.setSearchFilter}
							throttle={50}
						/>
					</PageHeader.Section>
					<PageHeader.Section align="right">
						{this.renderImportButtons()}
					</PageHeader.Section>
				</PageHeader>
				<DataTable
					clickable
					sortable
					columns={columns}
					items={rows}
					rowClick={clickRow}
				/>
			</div>
		);
	}
}

APIsView.propTypes = {
	items: PropTypes.array.isRequired,
	apiSpecTypes: PropTypes.object.isRequired,
	onImportApi: PropTypes.func.isRequired
};

export default injectIntl(APIsView);
